/**
 * Surface API contracts (`/api/assistant/*`). Mirrors `Ncp.Api/Contracts/AssistantContracts.cs`; the API omits null properties
 * (`WhenWritingNull`), so every nullable field is optional here. Regenerate the full OpenAPI schema with `pnpm api:types` when
 * a contract changes and reconcile this file against it.
 */
import type { AiProposal, Citation } from "@ncp/api-client";

// ---------------------------------------------------------------- catalog / shell
export type SideEffect = "Read" | "Confirm" | "Reviewed";

export interface AssistantDelegate {
  key: string;
  name: string;
  nameAr: string;
  category: string;
  purpose: string;
  purposeAr: string;
}

export interface SuggestedPrompt {
  text: string;
  textAr?: string | null;
  icon?: string | null;
}

export interface AssistantSummary {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  color: string;
  greeting?: string | null;
  greetingAr?: string | null;
  /** Shown under the composer; empty falls back to the platform's generic line. */
  disclaimer?: string | null;
  disclaimerAr?: string | null;
  /** Either plain strings or `{ text, textAr, icon }` objects, as configured in the console. */
  suggestedPrompts: Array<string | SuggestedPrompt>;
  allowAttachments: boolean;
  allowVoice: boolean;
  maxSideEffect: SideEffect;
  delegates: AssistantDelegate[];
  playbookKeys: string[];
  version: number;
}

export interface WorkspaceLink {
  solutionKey: string;
  solutionName: string;
  solutionNameAr?: string | null;
  icon: string;
  linkKey: string;
  label: string;
  labelAr?: string | null;
  url: string;
  /** True when the target app accepts the one-time SSO handoff code. */
  handoff: boolean;
}

/** Deep-link scopes of the solution registry (what a link points at). */
export type DeepLinkScope = "home" | "workspace" | "case" | "matter" | "document" | "proposal" | "thread" | "artifact" | "assistant";

/** Target of `GET /api/assistant/links`: the ids that apply; the server checks each against what the caller may see. */
export interface DeepLinkTarget {
  scope: DeepLinkScope;
  caseId?: string | null;
  threadId?: string | null;
  proposalId?: string | null;
  documentId?: string | null;
  artifactId?: string | null;
}

/** Registry links for one target. A `case` request for a playbook matter comes back as the `matter` scope (`isMatter`). */
export interface DeepLinks {
  scope: DeepLinkScope;
  workspaceKey?: string | null;
  caseId?: string | null;
  isMatter: boolean;
  links: WorkspaceLink[];
}

export interface WorkspaceSummary {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  color: string;
  kind: "Department" | "Function" | string;
  parentKey?: string | null;
  isOwner: boolean;
  /** How the caller is a member: `Owner`, `Member`, `Role`, `Admin`. */
  via: string;
  defaultAssistantKey?: string | null;
  assistants: AssistantSummary[];
  knowledgeCollectionKeys: string[];
  playbookKeys: string[];
  links: WorkspaceLink[];
  monthlyBudgetUsd: number;
  spentThisMonthUsd: number;
  pendingProposals: number;
  openThreads: number;
  /** The space manifest: home, navigation, matter tabs by stage, workbench and use cases, with the published pages they name. */
  space?: SpaceInfo;
}

/** A label in both languages as a space manifest carries it. */
export interface SpaceText {
  en: string;
  ar?: string | null;
}

export interface SpaceHomeSection {
  /** `builtin` (a widget such as `ask`), `page` (a page in the home slot), `widget` (one widget of a page) or `use-cases`. */
  kind: "builtin" | "page" | "widget" | "use-cases" | string;
  key?: string | null;
  page?: string | null;
  widget?: string | null;
  title?: SpaceText | null;
  span?: number;
}

export interface SpaceNavEntry {
  key: string;
  /** A page opened at `/p/<page>`. */
  page?: string | null;
  /** A built-in screen: home, chat, inbox, matters, watchers, artifacts, knowledge, evidence, tracker, library. */
  builtin?: string | null;
  label?: SpaceText | null;
  icon?: string | null;
  section: "work" | "insight" | "resources" | string;
  permission?: string | null;
  group?: string | null;
  /** How the app launcher shows a page entry: a tagline and up to three live figures. */
  launcher?: SpaceLauncher | null;
  /** A desk entry's ECONIC block as space.json writes it: the intelligence modules the desk draws on (BRIEF §1.4). */
  econic?: { modules?: { primary?: string[]; secondary?: string[] } } & Record<string, unknown> | null;
}

/**
 * A live figure on a launcher tile, read through a read-only capability as the person looking. `value` picks the figure out of the
 * result (`events` counts an array, `events[status=Live]` counts the matching items, a path reads a number or a text); `alert` draws a
 * figure above zero as needing attention and `badge` also shows it in the navigation. Mirrors `SpaceManifest.cs` `SpaceLauncherStat`.
 */
export interface SpaceLauncherStat {
  label: SpaceText;
  capability: string;
  input?: Record<string, unknown> | null;
  value?: string | null;
  format?: string | null;
  alert?: boolean;
  badge?: boolean;
}

/** A page's tile in the app launcher, as the manifest declares it: one line on what it is for and up to three live figures. */
export interface SpaceLauncher {
  tagline?: SpaceText | null;
  stats?: SpaceLauncherStat[] | null;
}

export interface SpaceMatterTab {
  builtin?: string | null;
  page?: string | null;
  label?: SpaceText | null;
  icon?: string | null;
  /** A key the matter's use-case template must have for the tab to show. */
  when?: string | null;
}

export interface SpaceTabGroup {
  stage: string;
  label?: SpaceText | null;
  tabs: SpaceMatterTab[];
  /** Matter states that open on this group. */
  states?: string[] | null;
}

export interface SpaceUseCase {
  key: string;
  title: SpaceText;
  description?: SpaceText | null;
  icon?: string | null;
  playbook?: string | null;
  page?: string | null;
  prompt?: string | null;
  parameters?: Record<string, unknown> | null;
  produces?: SpaceText | null;
  duration?: string | null;
  tags?: string[] | null;
  status?: string | null;
  /** What a matter opened from this use case starts with (questions, indicators, data bindings, the outputs pack…), read by its pages. */
  template?: Record<string, unknown> | null;
}

/** A published page a space refers to. */
export interface SpacePage {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  slots: string[];
}

export interface SpaceInfo {
  home: SpaceHomeSection[];
  nav: SpaceNavEntry[];
  matterTabs: Record<string, SpaceTabGroup[]>;
  workbench: string[];
  useCases: SpaceUseCase[];
  pages: SpacePage[];
  theme?: string | null;
  vocabulary?: string | null;
  /** Where the manifest came from: a repository pack (`repo:…`), the space designer (`console`), or `generated` for a workspace that had none. */
  source: string;
  /** The space's ECONIC block as space.json writes it: `classification` is the floor of its desks ("internal"). */
  econic?: { classification?: string } & Record<string, unknown> | null;
}

// ---------------------------------------------------------------- apps (pages in a space's navigation, as the launcher shows them)
// `GET /api/assistant/workspaces/{key}/apps` and `…/apps/{page}`; mirrors `SpaceAppDto` and its children (`AssistantContracts.cs`).

/** A live figure on a tile, already read for the caller. A figure that could not be read in time is left out, never sent as zero. */
export interface SpaceAppStat {
  /** The figure's position in the manifest's `launcher.stats`. */
  index: number;
  label: SpaceText;
  value: number | string | boolean | null;
  format?: string | null;
  alert: boolean;
  badge: boolean;
}

export interface SpaceAppAgent {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tier: string;
}

/** A job of the page: when it runs (a cron schedule in UTC or a platform event), what runs it, and how its last run went. */
export interface SpaceAppJob {
  key: string;
  title: string;
  titleAr?: string | null;
  schedule?: string | null;
  trigger?: string | null;
  runs: string;
  lastRunAt?: string | null;
  lastStatus?: string | null;
}

/** Opens in the space over the last 30 days, the people behind them, and when the caller last opened it. */
export interface SpaceAppUsage {
  opens: number;
  people: number;
  lastOpenedByMe?: string | null;
}

/** A page in the space's navigation as its launcher tile draws it. `key` is the navigation key; `name` is the page's title. */
export interface SpaceApp {
  key: string;
  page: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  section: "work" | "insight" | "resources" | string;
  group?: string | null;
  tagline?: SpaceText | null;
  version: number;
  publishedAt?: string | null;
  publishedByName?: string | null;
  tags: string[];
  validationStatus?: string | null;
  hasThumbnail: boolean;
  stats: SpaceAppStat[];
  agents: SpaceAppAgent[];
  jobs: SpaceAppJob[];
  usage: SpaceAppUsage;
}

/** Where the space shows the page: `nav`, `home`, `tab` (a matter tab, with its playbook and stage), `workbench` or `use-case`. */
export interface SpaceAppPlacement {
  kind: string;
  label?: SpaceText | null;
  playbook?: string | null;
  stage?: string | null;
  key?: string | null;
}

/** A collection or dataset the page keeps in this space. */
export interface SpaceAppData {
  kind: string;
  key: string;
  name: string;
  nameAr?: string | null;
  scope?: string | null;
  classification: string;
  rows?: number | null;
  refreshedAt?: string | null;
}

/** Another space the page is published in. */
export interface SpaceAppSpace {
  key: string;
  name: string;
  nameAr?: string | null;
  icon: string;
  color: string;
}

export interface SpaceAppVersion {
  version: number;
  status: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  changeNote?: string | null;
}

/** The About view of a page in a space: the tile plus where the page appears, its data, what it may change, other spaces and its versions. */
export interface SpaceAppDetail {
  app: SpaceApp;
  slots: string[];
  capabilities: string[];
  grants: string[];
  placements: SpaceAppPlacement[];
  data: SpaceAppData[];
  alsoIn: SpaceAppSpace[];
  versions: SpaceAppVersion[];
}

export interface AssistantCatalog {
  workspaces: WorkspaceSummary[];
  /** Effective `assistant.*` / `knowledge.*` / `matters.*` permissions of the caller. */
  permissions: string[];
  defaultWorkspaceKey?: string | null;
  offline: boolean;
  provider: string;
  generatedAt: string;
}

// ---------------------------------------------------------------- home
export interface HomeThread {
  id: string;
  workspaceKey: string;
  assistantKey: string;
  title: string;
  lastMessagePreview?: string | null;
  lastMessageAt: string;
  messageCount: number;
  pinned: boolean;
  caseId?: string | null;
}

export interface HomeRun {
  id: string;
  threadId?: string | null;
  threadTitle?: string | null;
  agentKey: string;
  agentName: string;
  status: "Queued" | "Running" | "Succeeded" | "Failed" | "Cancelled" | string;
  startedAt: string;
  completedAt?: string | null;
  proposalId?: string | null;
  error?: string | null;
}

export interface HomeArtifact {
  id: string;
  threadId: string;
  workspaceKey: string;
  key: string;
  version: number;
  kind: ArtifactKind;
  title: string;
  titleAr?: string | null;
  updatedAt: string;
}

export interface HomeUsage {
  spentThisMonthUsd: number;
  budgetUsd: number;
  turnsThisMonth: number;
  turnsToday: number;
  spentTodayUsd: number;
}

export interface HomeSuggestion {
  text: string;
  textAr?: string | null;
  icon?: string | null;
  assistantKey: string;
}

export interface Home {
  workspaceKey?: string | null;
  generatedAt: string;
  recentThreads: HomeThread[];
  pinnedThreads: HomeThread[];
  pendingProposals: number;
  latestProposals: AiProposal[];
  runningAgents: HomeRun[];
  recentlyCompleted: HomeRun[];
  recentArtifacts: HomeArtifact[];
  suggestions: HomeSuggestion[];
  usage: HomeUsage;
  knowledgeDocuments: number;
  knowledgeCollections: number;
}

// ---------------------------------------------------------------- threads
export type ThreadStatus = "Active" | "Archived";
export type MessageRole = "User" | "Assistant" | "System" | "Tool";
export type ArtifactKind = "Markdown" | "Table" | "Chart" | "Docx" | "Pdf" | "Xlsx" | "Json" | string;

export interface Thread {
  id: string;
  workspaceKey: string;
  assistantKey: string;
  title: string;
  titleAr?: string | null;
  caseId?: string | null;
  caseNumber?: string | null;
  surfaceKey: string;
  channel: string;
  status: ThreadStatus;
  pinned: boolean;
  messageCount: number;
  lastMessageAt: string;
  lastMessagePreview?: string | null;
  locale: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  createdAt: string;
  /** The case is a playbook matter worked in the AI Workspace (`/matters/{caseId}`), not a back-office case. Set on thread detail. */
  isMatter?: boolean;
  /** The ECONIC project the conversation belongs to (`/projects/{projectId}`), if any. */
  projectId?: string | null;
  projectName?: string | null;
  /** The conversation's author (its owner). */
  ownerUserId?: string;
  ownerName?: string | null;
  /** The caller is a member of the conversation's project but not its author: they read it, only the author posts. */
  readOnly?: boolean;
}

export interface ToolCallTrace {
  tool: string;
  agentKey?: string | null;
  argsSummary?: string | null;
  resultSummary?: string | null;
  durationMs: number;
  status: string;
}

export interface MessageCitation {
  index: number;
  sourceType: string;
  sourceId: string;
  title: string;
  locator?: string | null;
  excerpt?: string | null;
  documentId?: string | null;
  chunkId?: string | null;
  documentKey?: string | null;
  /** Knowledge Bank sources (builder §17.6, KB §7.2): the page, the version cited, its portal and sensitivity. */
  page?: number | null;
  versionId?: string | null;
  versionNumber?: number | null;
  portalKey?: string | null;
  sensitivity?: string | null;
  /** A restricted passage: the assistant saw the title only and the excerpt is empty. */
  withheld?: boolean;
  /**
   * A statistics value or a web page (22 Oct 2026): the page a person opens, the publisher, the period the value is for, the publisher's
   * update and the date ECONIC last checked it (yyyy-MM-dd). The locator keeps the period alone; older answers carry none of these.
   */
  url?: string | null;
  publisher?: string | null;
  period?: string | null;
  published?: string | null;
  checked?: string | null;
}

export interface MessageAttachment {
  documentId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface ThreadMessage {
  id: string;
  sequence: number;
  role: MessageRole;
  content: string;
  toolCalls: ToolCallTrace[];
  citations: MessageCitation[];
  attachments: MessageAttachment[];
  proposalIds: string[];
  artifactIds: string[];
  confirmationId?: string | null;
  runId?: string | null;
  agentKey?: string | null;
  modelId?: string | null;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  latencyMs: number;
  guardrailFlags: string[];
  offline: boolean;
  feedback?: number | null;
  feedbackNote?: string | null;
  error?: string | null;
  createdAt: string;
  /** Charts, tables and other UI blocks the answer rendered (`ui_render`). */
  blocks?: AnswerBlock[] | null;
  /** ECONIC extras (builder §6.4, §17): the screen a message was sent from, the builder's changes, the agent's questions, handoffs. */
  extras?: MessageExtras | null;
}

export interface Artifact {
  id: string;
  threadId: string;
  messageId?: string | null;
  workspaceKey: string;
  key: string;
  version: number;
  previousVersionId?: string | null;
  kind: ArtifactKind;
  title: string;
  titleAr?: string | null;
  /** Empty in list responses; fetch the artifact by id for the body. */
  content: string;
  contentAr?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  hasExport: boolean;
  agentKey?: string | null;
  runId?: string | null;
  proposalId?: string | null;
  caseId?: string | null;
  caseDocumentId?: string | null;
  isCurrent: boolean;
  editedByName?: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  /** Formats the server can render this artifact to; the first is the default. */
  exportFormats: ArtifactExportFormat[];
}

export type ArtifactExportFormat = "docx" | "pdf" | "xlsx" | "pptx" | "zip" | "md" | "csv" | "json";

export interface AttachArtifactRequest {
  matterId: string;
  format?: ArtifactExportFormat;
  locale?: "en" | "ar";
  category?: string;
}

export interface AttachArtifactResult {
  documentId: string;
  fileName: string;
  matterId: string;
  caseNumber: string;
  artifact: Artifact;
}

export interface ToolConfirmation {
  id: string;
  threadId: string;
  messageId: string;
  toolKey: string;
  title: string;
  titleAr?: string | null;
  description: string;
  descriptionAr?: string | null;
  /** Keys starting with `_` are internal (ids the action targets); the rest is what the card shows and the user may reword. */
  arguments: Record<string, unknown>;
  status: "Pending" | "Confirmed" | "Declined" | "Expired" | "Executed" | "Failed" | string;
  expiresAt: string;
  decidedAt?: string | null;
  resultSummary?: string | null;
  error?: string | null;
  /** What the executed action produced; `link` is surface-relative (`/matters/{id}`). */
  result?: { result?: unknown; caseId?: string | null; link?: string | null; summaryAr?: string | null } | null;
}

export interface DecideConfirmationResponse {
  confirmation: ToolConfirmation;
  /** The outcome line posted into the thread. */
  message: ThreadMessage;
}

export interface ThreadDetail {
  thread: Thread;
  assistant?: AssistantSummary | null;
  messages: ThreadMessage[];
  artifacts: Artifact[];
  pendingConfirmations: ToolConfirmation[];
  runs: HomeRun[];
  /** Every confirmation of the thread, pending and decided. */
  confirmations: ToolConfirmation[];
}

export interface CreateThreadRequest {
  workspaceKey: string;
  assistantKey?: string | null;
  caseId?: string | null;
  title?: string | null;
  surfaceKey?: string | null;
  channel?: string | null;
  locale?: string | null;
}

export interface PatchThreadRequest {
  title?: string | null;
  pinned?: boolean | null;
  status?: ThreadStatus | null;
}

export interface SendMessageRequest {
  message?: string | null;
  locale?: string | null;
  attachmentIds?: string[] | null;
  /** Steers the turn (`delegate`, `brief`, `formal`…); interpreted by the orchestrator, never shown to the model as user text. */
  directive?: string | null;
  /** The custom page open next to the conversation: the turn acts in it (its tools, agents and data) and knows what it shows. */
  page?: OpenPage | null;
  /** What the ECONIC screen shows (builder §17.7): ≤ 20 visible widgets with ≤ 6 figures each, ≤ 16 KB. The server re-derives every
   * widget's classification, strips Restricted figures and drops Confidential widgets before the model sees it. */
  surface?: SurfaceState | null;
  /** "Regenerate" or "Edit and resend": the id of the thread's last question, which this message replaces with its answer. */
  replaces?: string | null;
  /** The sources of knowledge the turn may read (the ECONIC sources row): only their tools are offered to the model. The row's two names map
   * to the wire as Knowledge Bank = `kb` and Open Web = `data` + `web`. Omitted on an ECONIC screen: all three, as the row starts
   * (Country360's surface: the Knowledge Bank only). Narrows what the agent reads, never who may read what. */
  sources?: KnowledgeSourceKey[] | null;
  /** The Knowledge Bank datasets the turn may read ("Choose datasets…"): the collection keys and table keys (`kb:<id>`) ticked, each its
   * own reach (a collection its documents, a table its rows). Omitted or empty: every dataset the person may read. Only read while `kb` is
   * among the sources; the server restricts the knowledge tools to them. At most 50. */
  datasets?: string[] | null;
  /** Or the datasets left out (every other one in reach), when that list is the shorter: read only when `datasets` is empty. At most 50. */
  datasetsExcept?: string[] | null;
}

/** `kb` the Ministry Knowledge Bank (its documents, its tables, Country360's federal datasets) · `data` open data (the statistics warehouse
 * and the live official sources) · `web` web search · `subs` the Ministry's subscriptions (Bloomberg, S&P Global, Oxford Economics…:
 * licensed, read only when chosen). The ECONIC sources row shows `data` and `web` together as "Open Web", and `subs` as "Subscribed". */
export type KnowledgeSourceKey = "kb" | "data" | "web" | "subs";

/** What a composer hands over at send time: the sources and, under the Knowledge Bank, the datasets ticked or those left out (neither: all). */
export interface TurnSourceChoice { sources: KnowledgeSourceKey[]; datasets?: string[] | null; datasetsExcept?: string[] | null }

/** The sources and datasets of a send, from what the host's `sources` callback returned (a bare key list or a {@link TurnSourceChoice}). */
export function turnSourcesOf(v: KnowledgeSourceKey[] | TurnSourceChoice | null | undefined): { sources?: KnowledgeSourceKey[]; datasets?: string[]; datasetsExcept?: string[] } {
  if (!v) return {};
  const sources = Array.isArray(v) ? v : v.sources;
  const datasets = Array.isArray(v) ? null : v.datasets;
  const except = Array.isArray(v) ? null : v.datasetsExcept;
  const kb = !!sources?.includes("kb");
  return {
    ...(sources?.length ? { sources } : {}),
    ...(kb && datasets?.length ? { datasets: datasets.slice(0, 50) } : kb && except?.length ? { datasetsExcept: except.slice(0, 50) } : {}),
  };
}

/** A custom page open next to the copilot: its page session token (proves the person opened it) and what it reports it shows. */
export interface OpenPage {
  token: string;
  state?: Record<string, unknown> | null;
}

/**
 * Where an answer block belongs (the inline-widget contract, 17 Oct 2026): "inline" is content drawn only where the text places its marker
 * `[[w:<id>]]`; "progress" is the work as it runs (the plan, the team), shown in the live steps line and never in the body; "tail" follows
 * the answer (next steps, follow-ups, links). Absent on messages saved before the contract.
 */
export type BlockPlacement = "inline" | "progress" | "tail";

/** A UI block (chart, table, KPI strip, map…) an agent rendered into its answer; page-kit draws it. */
export type AnswerBlock = Record<string, unknown> & { id?: string; type?: string; placement?: BlockPlacement | null };

/** One of the open page's client tools the agent called; the page runs it and the host posts its answer back. */
export interface ClientToolCall {
  callId: string;
  tool: string;
  arguments: Record<string, unknown>;
}

export interface ThreadFilter {
  workspace?: string;
  status?: ThreadStatus;
  q?: string;
  caseId?: string;
  pinned?: boolean;
  take?: number;
  /** Only threads opened from this surface (`page-copilot:<page key>`). */
  surface?: string;
}

export interface UploadedAttachment {
  documentId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  characters: number;
  usedVision: boolean;
  /** How far the text is read: the rest of a large file is read in the background ("reading"), or only part could be read ("partial"). */
  state?: "ready" | "reading" | "indexing" | "partial";
  /** What the person should know about the file, when it matters. */
  note?: string | null;
}

export interface AttachmentUploadResult {
  attachments: UploadedAttachment[];
  errors: string[];
}

export interface EditArtifactRequest {
  content: string;
  title?: string | null;
  titleAr?: string | null;
  contentAr?: string | null;
}

export interface ArtifactFilter {
  workspace?: string;
  threadId?: string;
  kind?: ArtifactKind;
  q?: string;
  take?: number;
}

// ---------------------------------------------------------------- inbox
export interface InboxItem {
  proposal: AiProposal;
  workspaceKey?: string | null;
  threadId?: string | null;
  threadTitle?: string | null;
}

export interface InboxPage {
  items: InboxItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface InboxFilter {
  workspace?: string;
  status?: string;
  agentKey?: string;
  threadId?: string;
  /** One proposal, whatever its status (deep link). */
  proposalId?: string;
  page?: number;
  pageSize?: number;
}

export interface InboxSummary {
  pending: number;
  approved: number;
  edited: number;
  rejected: number;
  byWorkspace: Array<{ workspaceKey: string; count: number }>;
  byAgent: Array<{ agentKey: string; agentName: string; count: number }>;
}

// ---------------------------------------------------------------- knowledge
export interface KnowledgeCollection {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  workspaceKey?: string | null;
  visibility: "Public" | "Internal" | "AgentsOnly" | string;
  defaultDocumentKind: string;
  uploadRoleKeys: string[];
  documents: number;
  chunks: number;
  lastDocumentAt?: string | null;
  lastIngestedAt?: string | null;
  lastIngestionError?: string | null;
  canUpload: boolean;
  canManage: boolean;
  isBuiltIn: boolean;
}

export interface KnowledgeDocument {
  id: string;
  key: string;
  title: string;
  titleAr: string;
  kind: string;
  collectionKey: string;
  issuer?: string | null;
  issuedOn?: string | null;
  inForceFrom?: string | null;
  language: string;
  sourceUrl?: string | null;
  summary?: string | null;
  chunkCount: number;
  isActive: boolean;
  uploadedByName?: string | null;
  hasFile: boolean;
  createdAt: string;
  updatedAt: string;
  /** The collection source that files and refreshes this document; absent for uploads and seeds. */
  sourceKey?: string | null;
}

export interface KnowledgeChunk {
  id: string;
  sequence: number;
  locator: string;
  heading?: string | null;
  text: string;
  textAr?: string | null;
}

export interface KnowledgeDocumentDetail {
  document: KnowledgeDocument;
  chunks: KnowledgeChunk[];
  downloadUrl?: string | null;
}

export interface KnowledgeUploadResult {
  document: KnowledgeDocument;
  pageCount: number;
  usedVision: boolean;
}

export interface ToolDescriptor {
  key: string;
  description: string;
  sideEffect: SideEffect;
  source: string;
}

// ---------------------------------------------------------------- streaming events (SSE `data:` frames of POST /threads/{id}/messages)
export type AssistantStreamEvent =
  /** The turn's first frame: the id its saved answer will have (the live answer keeps one key from here to its saved form), and its layout:
   *  "inline-1" when the answer places its widgets in its text (the saved message will carry `extras.layout = "inline-1"`), else null. */
  | { type: "start"; messageId: string; layout?: string | null }
  | { type: "token"; text: string }
  | { type: "status"; stage: string; detail?: string | null }
  | { type: "tool"; tool: string; phase: "start" | "end" | "error" | string; summary?: string | null; durationMs?: number | null; agentKey?: string | null }
  | { type: "citation"; index: number; sourceType: string; sourceId: string; title: string; locator: string; excerpt: string; documentId?: string | null; chunkId?: string | null; documentKey?: string | null;
      page?: number | null; versionId?: string | null; versionNumber?: number | null; withheld?: boolean; portalKey?: string | null; sensitivity?: string | null;
      url?: string | null; publisher?: string | null; period?: string | null; published?: string | null; checked?: string | null }
  | { type: "proposal"; proposalId: string; agentKey: string; title: string; confidence: number; status: string }
  | { type: "artifact"; artifactId: string; key: string; version: number; artifactKind: ArtifactKind; title: string }
  /** A document being written in the person's pane (an ECONIC economic study, 22 Oct 2026): `text` is the next piece of its body; `done`
   *  closes the stream once it is saved (the saved version then replaces the live text: its source markers renumbered, its sources listed). */
  | { type: "artifact_delta"; artifactId: string; text: string; done?: boolean }
  | { type: "confirm"; confirmationId: string; tool: string; title: string; description: string; argumentsJson: string; expiresAt: string }
  | { type: "delegation"; runId?: string | null; agentKey: string; agentName: string; async: boolean }
  | { type: "final"; messageId: string; content: string; guardrailFlags: string[]; modelId: string; inputTokens: number; outputTokens: number; costUsd: number; latencyMs: number; offline: boolean; title?: string | null }
  | { type: "error"; message: string; code?: string | null }
  | { type: "block"; blockId: string; block: AnswerBlock; placement?: BlockPlacement | null }
  | ({ type: "client_call" } & ClientToolCall)
  | ({ type: "ui" } & UiEvent)
  | ({ type: "ask_user" } & AskUserEvent);

// ---------------------------------------------------------------- ECONIC Agent (builder §15.4, §17; the sidebar's client contract)

export type SurfaceKind = "dashboard" | "workspaces" | "module" | "knowledge" | "tasks" | "trust" | "inbox" | "app" | "matter" | "chat";

/** One figure a widget shows, as the agent is told about it (a Restricted one is sent as its label only). */
export interface SurfaceFigure {
  label: string;
  value?: string | number;
  unit?: string;
  period?: string;
  source?: string;
  withheld?: true;
}

/** What "✦ Dive in" points at (modules-a §1.5.1): a widget, a cell, a row, a document or an answer block. */
export interface SurfaceFocus {
  module?: string;
  tab?: string;
  widgetId?: string;
  cell?: string;
  rowKey?: string;
  documentId?: string;
  title?: string;
  figures?: SurfaceFigure[];
  lineage?: unknown[];
  classification?: string;
}

export interface SurfaceWidget {
  id: string;
  title: string;
  type: string;
  ref?: string;
  source?: string;
  asOf?: string;
  /** The resolver's classification of the widget; the server re-derives it and the host's claim can only raise it. */
  classification?: string;
  figures?: SurfaceFigure[];
  summary?: string;
}

/** The screen the person is on, sent with each message (builder §17.7). `route` starts with `/`. */
export interface SurfaceState {
  kind: SurfaceKind;
  surface: string;
  route: string;
  title: string;
  titleAr?: string | null;
  /** The department or workspace key the screen belongs to (C16). */
  department: string;
  layoutVersion?: { system?: number; department?: number; me?: number };
  focus?: SurfaceFocus;
  filters?: Record<string, unknown>;
  widgets?: SurfaceWidget[];
}

/** What the model was actually given about the screen: the `status` frame with `stage: "surface"` (the first frame of a turn). */
export interface SurfaceSeen {
  widgets: number;
  sent: number;
  withheld: string[];
  dropped: string[];
  highest?: string | null;
  focusWithheld?: boolean;
}

export type UiEventScope = "me" | "department" | "global" | "pref";
export type UiEventStatus = "applied" | "drafted" | "proposed" | "published" | "restored" | "undone";

/** A change the builder tools made to what the person sees (builder §15.4); applied by the host when the turn settles. */
export interface UiEvent {
  surface: string;
  scope: UiEventScope | string;
  status: UiEventStatus | string;
  version?: number | null;
  draftId?: string | null;
  proposalId?: string | null;
  summary: string;
  summaryAr: string;
  changed: string[];
  space?: string | null;
  /** A department draft opens here (`<route>?layoutPreview=<draftId>`). */
  previewRoute?: string | null;
}

export interface AskOption { label: string; detail?: string | null; recommended?: boolean }
export interface AskQuestion { id: string; question: string; multi: boolean; options: AskOption[] }
/** Up to three multiple-choice questions the agent asks before acting; the turn ends with them (builder §15.3 `ask_user`). */
export interface AskUserEvent { intro?: string | null; questions: AskQuestion[] }

/** The screen a user message was sent from (`extras.context`); the sidebar draws "now on …" dividers from it. */
export interface MessageContext {
  kind?: string | null;
  route?: string | null;
  surface?: string | null;
  title?: string | null;
  titleAr?: string | null;
  space?: string | null;
}

export interface MessageHandoff {
  fromThreadId?: string | null;
  fromSpace?: string | null;
  fromName?: string | null;
  toThreadId?: string | null;
  toSpace?: string | null;
  toName?: string | null;
}

export interface MessageExtras {
  context?: MessageContext | null;
  answers?: { text: string } | null;
  ui?: UiEvent[] | null;
  ask?: AskUserEvent | null;
  handoff?: MessageHandoff | null;
  /** A system line that separates two parts of the thread ("Moved to New Economy."). */
  divider?: boolean | null;
  /** A turn a later "Regenerate" or "Edit and resend" replaced: kept on the server, left out of the transcript. */
  superseded?: boolean | null;
  /** An answer's sources of knowledge, as the person chose them for its turn ("Show your work"). */
  sources?: KnowledgeSourceKey[] | null;
  /** The sources the answer's tools actually read (`kb`, `data`, `web`, `subs`), in that order: "Sources used". */
  sourcesUsed?: KnowledgeSourceKey[] | null;
  /** The Knowledge Bank datasets the turn was restricted to, by name (absent: every dataset). */
  datasets?: Array<{ key: string; name: string; nameAr?: string | null }> | null;
  /** Or the Knowledge Bank datasets the turn left out, by name (every other one was in reach). */
  datasetsExcept?: Array<{ key: string; name: string; nameAr?: string | null }> | null;
  /** "inline-1": the text places its widgets with markers (`[[w:<id>]]` alone on a line); absent on earlier answers (the earlier layout). */
  layout?: string | null;
  /** Content the tools drew that the text did not place (kept for the record, never drawn in the body). */
  unplaced?: Array<{ id: string; kind?: string | null; title?: string | null }> | null;
  /** The server placed a widget the text did not (a draft is always shown). */
  autoPlaced?: boolean | null;
  [key: string]: unknown;
}

/** `POST /api/assistant/sidebar/thread`: the department's follow-me conversation (surface `econic:<space>`). */
export interface SidebarThreadRequest { space: string; locale?: string | null; fresh?: boolean }
export interface SidebarThreadResponse { threadId: string; created: boolean; space: string; assistantKey: string; surfaceKey: string }

/** `GET /api/assistant/agent/specialists`: who the ECONIC Agent screen can talk to (WP-SUPER-AGENT). In `specialists` (the role specialists)
 *  `module` is null; in `agents` (ECONIC's five agents, 16 Oct 2026) `module` is their module key (`kb`, `uae-radar`, `country360`,
 *  `outlook`, `global-radar`), which is also their key, `role` their caption ("Economist") and `serves` their remit. Policy (PMO) is the
 *  PMO's agent and is never listed. */
export interface AgentSpecialist {
  key: string; name: string; nameAr: string; role: string; roleAr: string; serves: string; servesAr: string; icon: string; module?: string | null;
}
/** A desk's own agent, talked to directly with the key `desk:<n>` (its role and what to ask it come with it). */
export interface AgentDeskAgent {
  n: number; key?: string; page: string; name: string; nameAr: string; agent: string; agentName: string; agentNameAr: string;
  role?: string | null; roleAr?: string | null; prompts?: Array<{ text: string; textAr?: string | null }>; href: string; space: string;
}
export interface AgentRoster { specialists: AgentSpecialist[]; agents?: AgentSpecialist[]; desks: AgentDeskAgent[]; viewOnly: boolean }

/**
 * The specialist a thread talks to, from its surface (`econic:<space>:agent:<key>`; the Librarian about one theme
 * `…:agent:kb:theme:<topic>`), or null for the ECONIC Agent itself.
 */
export function specialistOfSurface(surfaceKey: string | null | undefined): string | null {
  const at = surfaceKey?.indexOf(":agent:") ?? -1;
  return at >= 0 ? surfaceKey!.slice(at + ":agent:".length).split(":")[0] || null : null;
}

/** The theme a conversation with the Librarian is about (`…:agent:kb:theme:<topic>`, opened from a theme's page), or null. */
export function themeOfSurface(surfaceKey: string | null | undefined): string | null {
  const m = /:agent:kb:theme:([a-z0-9][a-z0-9-]{0,95})$/.exec(surfaceKey ?? "");
  return m ? m[1] : null;
}

/** The desk whose agent a thread talks to, from its surface (`econic:<space>:desk:<n>`), or null. */
export function deskOfSurface(surfaceKey: string | null | undefined): number | null {
  const m = /:desk:(\d{1,2})$/.exec(surfaceKey ?? "");
  return m ? Number(m[1]) : null;
}

/** Who a thread talks to on the ECONIC Agent screen: a specialist's key, `desk:<n>` for a desk's agent, or null for the ECONIC Agent. */
export function partnerOfSurface(surfaceKey: string | null | undefined): string | null {
  const desk = deskOfSurface(surfaceKey);
  return desk ? `desk:${desk}` : specialistOfSurface(surfaceKey);
}

/** `POST /api/assistant/threads/{id}/handoff`: "Bring this conversation" into another department. */
export interface ThreadHandoffResponse { threadId: string; sourceThreadId: string; toSpace: string; summary: string; messageId: string }

export type { AiProposal, Citation };
